import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { getIn, useFormikContext } from 'formik';
import type { FormikValues } from 'formik/dist/types';
import { omit, set } from 'lodash';
import * as Yup from 'yup';

import {
  BUSINESS_VALIDATION_STATUSES,
  BUSINESS_VALIDATION_TYPE,
  REVERTABLE_BUSINESS_STATUSES,
} from '~/components/Contacts/businessValidationStatuses';
import BusinessVerificationBadge from '~/components/Contacts/BusinessVerificationBadge/BusinessVerificationBadge';
import type { Tab } from '~/components/Contacts/UpsertContact/types';
import { useContact } from '~/components/Contacts/UpsertContact/useContact';
import type {
  GbgBusinessFindSuggestion,
  GbgBusinessRetrieveResult,
  GbgBusinessRetrieveValidation,
} from '~/components/core/AddressAutocomplete/GbgAddressAutocomplete/types';
import {
  useGbgBusinessFind,
  useGbgBusinessRetrieve,
} from '~/components/core/AddressAutocomplete/GbgAddressAutocomplete/useGbgBusinessAutocomplete';
import Autocomplete from '~/components/core/Molecules/Fields/AutoComplete';
import Text from '~/components/core/TextComponents/Text';
import { ErrorIcon } from '~/components/icons';
import TextFieldFormik from '~/components/TextFieldFormik';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';

import { useFieldsValidationRevert } from '../../useFieldsValidationRevert';
import { getInitializedValues, normalizeOptionalString as norm } from '../../utils';
import TabWrapper from '../TabWrapper';

import { BUSINESS_FIELDS_TO_WATCH, BUSINESS_TAB_KEY, FORMIK_BUSINESS_FIELD_IDS } from './constants';
import TEXTS from './texts';

const getFullFieldPath = (fieldId: string) => `${BUSINESS_TAB_KEY}.${fieldId}`;

const useBusinessSessionValidation = (): {
  sessionValidation: GbgBusinessRetrieveValidation | undefined;
} => {
  const { values } = useFormikContext();
  const { originalContact } = useContact();
  const validatedInSessionRef = useRef(false);

  const formikValidation: GbgBusinessRetrieveValidation | null = getIn(
    values,
    getFullFieldPath(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_VALIDATION)
  );

  useEffect(() => {
    if (formikValidation) {
      validatedInSessionRef.current = true;
    }
  }, [formikValidation]);

  if (formikValidation) {
    return { sessionValidation: formikValidation };
  }

  const serverStatus = originalContact?.validations?.find(
    (v) => v.validation_type === BUSINESS_VALIDATION_TYPE
  )?.status;
  const wasPreviouslyValidated =
    validatedInSessionRef.current || REVERTABLE_BUSINESS_STATUSES.some((status) => status === serverStatus);

  if (!wasPreviouslyValidated) {
    return { sessionValidation: undefined };
  }

  const isBusinessDirty = BUSINESS_FIELDS_TO_WATCH.some(
    (field) => norm(getIn(values, getFullFieldPath(field))) !== norm(originalContact?.[field])
  );
  if (isBusinessDirty) {
    return {
      sessionValidation: {
        validation_type: 'business',
        status: BUSINESS_VALIDATION_STATUSES.NOT_VALIDATED,
        provider: 'gbg',
        validation_details: null,
      },
    };
  }

  return { sessionValidation: undefined };
};

interface BusinessVerificationHeaderProps {
  sessionValidation?: GbgBusinessRetrieveValidation;
  country: string;
}

const BusinessVerificationHeader: React.FC<BusinessVerificationHeaderProps> = ({ sessionValidation, country }) => {
  const { originalContact } = useContact();
  const validations = originalContact?.validations;

  return (
    <div className="col-span-full">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
        {TEXTS.disclaimerTitle}
      </Text>
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="mb-12 mt-2 flex gap-2">
        <ErrorIcon iconColor="currentColor" className="mt-2 shrink-0" />
        {TEXTS.disclaimerBody}
      </Text>
      <BusinessVerificationBadge validations={validations} sessionValidation={sessionValidation} country={country} />
    </div>
  );
};

interface BusinessAutocompleteProps {
  id: string;
  label: string;
  country: string;
  disabled: boolean;
  onBusinessSelected: (suggestion: GbgBusinessFindSuggestion) => void;
}

const BusinessAutocomplete: React.FC<BusinessAutocompleteProps> = ({
  id,
  label,
  country,
  disabled,
  onBusinessSelected,
}) => {
  const { values, setFieldValue, setFieldTouched, errors, touched } = useFormikContext();
  const { suggestions, isLoading, search, clearSuggestions } = useGbgBusinessFind({ country });

  const suggestionsDict = useMemo(() => {
    const dict: Record<string, GbgBusinessFindSuggestion> = {};
    for (const suggestion of suggestions) {
      dict[suggestion.id] = suggestion;
    }
    return dict;
  }, [suggestions]);

  const handleInputChanged = useCallback(
    (_event: React.SyntheticEvent | null, inputValue: string, reason: string) => {
      if (reason === 'input') {
        if (inputValue) {
          search(inputValue);
        } else {
          clearSuggestions();
        }
      }
      setFieldValue(id, inputValue);
    },
    [search, clearSuggestions, setFieldValue, id]
  );

  const handleSuggestionSelected = useCallback(
    (_: React.SyntheticEvent | undefined, optionKey?: string | null) => {
      if (!optionKey) return;

      const suggestion = suggestionsDict[optionKey];
      if (suggestion) {
        clearSuggestions();
        onBusinessSelected(suggestion);
      }
    },
    [suggestionsDict, clearSuggestions, onBusinessSelected]
  );

  const error = getIn(touched, id) && getIn(errors, id) ? getIn(errors, id) : undefined;

  return (
    <Autocomplete
      id={id}
      freeSolo
      options={Object.keys(suggestionsDict)}
      getOptionLabel={(optionKey: string) => suggestionsDict[optionKey]?.name || optionKey}
      onChange={handleSuggestionSelected}
      value={getIn(values, id) || null}
      disabled={disabled}
      loading={isLoading}
      onInputChange={handleInputChanged}
      label={label}
      textFieldProps={{
        error: !!error,
        helperText: error,
        onBlur: () => setFieldTouched(id, true),
      }}
    />
  );
};

const BusinessTab: React.FC = () => {
  const { isSubmitting, setFieldValue } = useFormikContext();
  const { currentCountry } = useContact();
  const country = currentCountry || 'AU';

  const { sessionValidation } = useBusinessSessionValidation();
  const { retrieve, isLoading: isRetrieving } = useGbgBusinessRetrieve({ country });

  useFieldsValidationRevert(
    BUSINESS_FIELDS_TO_WATCH,
    getFullFieldPath(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_VALIDATION),
    getFullFieldPath
  );

  const bnLabel = country === 'NZ' ? TEXTS.bnLabelNZ : TEXTS.bnLabelAU;
  const cnLabel = country === 'NZ' ? TEXTS.cnLabelNZ : TEXTS.cnLabelAU;

  const applyRetrieveResult = useCallback(
    (result: GbgBusinessRetrieveResult) => {
      setFieldValue(getFullFieldPath(FORMIK_BUSINESS_FIELD_IDS.AU_NZ_BUSINESS_NUMBER), result.bn);
      setFieldValue(getFullFieldPath(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_LEGAL_NAME), result.legal_name);
      setFieldValue(getFullFieldPath(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_TRADING_NAME), result.trading_name || '');
      setFieldValue(getFullFieldPath(FORMIK_BUSINESS_FIELD_IDS.AU_NZ_COMPANY_NUMBER), result.cn);
      setFieldValue(getFullFieldPath(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_VALIDATION), result.validation);
    },
    [setFieldValue]
  );

  const handleBusinessSelected = useCallback(
    (suggestion: GbgBusinessFindSuggestion) => {
      // Pre-fill the name while we retrieve full details
      setFieldValue(getFullFieldPath(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_LEGAL_NAME), suggestion.name);

      // bn is masked in find results — use the suggestion id to retrieve full details
      if (suggestion.id) {
        retrieve(suggestion.id).then((result) => {
          if (result) {
            applyRetrieveResult(result);
          } else {
            setFieldValue(getFullFieldPath(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_VALIDATION), null);
          }
        });
      }
    },
    [setFieldValue, retrieve, applyRetrieveResult]
  );

  const isBusy = isSubmitting || isRetrieving;

  return (
    <TabWrapper>
      <div className="mb-32 grid grid-cols-2 gap-20">
        <BusinessVerificationHeader sessionValidation={sessionValidation} country={country} />

        <BusinessAutocomplete
          id={getFullFieldPath(FORMIK_BUSINESS_FIELD_IDS.AU_NZ_BUSINESS_NUMBER)}
          label={bnLabel}
          country={country}
          disabled={isBusy}
          onBusinessSelected={handleBusinessSelected}
        />
        <BusinessAutocomplete
          id={getFullFieldPath(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_LEGAL_NAME)}
          label={TEXTS.businessLegalName}
          country={country}
          disabled={isBusy}
          onBusinessSelected={handleBusinessSelected}
        />

        <div className="col-span-full grid grid-cols-subgrid gap-y-16 rounded-2xl bg-slate-100 p-12">
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="col-span-full">
            {TEXTS.businessDetailsSection}
          </Text>
          <TextFieldFormik
            id={getFullFieldPath(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_TRADING_NAME)}
            label={TEXTS.businessTradingName}
            fullWidth
            disabled={isBusy}
          />
          <TextFieldFormik
            id={getFullFieldPath(FORMIK_BUSINESS_FIELD_IDS.AU_NZ_COMPANY_NUMBER)}
            label={cnLabel}
            fullWidth
            disabled={isBusy}
          />
        </div>
      </div>
    </TabWrapper>
  );
};

const useBusinessTab = (): Tab => {
  const { gbgValidationStatus, isCompany, currentCountry } = useContact();

  const getInitialValues = (contact?: ContactFullModel) => {
    const values = {};

    const setInitialValue = (fieldId: string, initialValue: unknown, defaultInitialValue: unknown = '') =>
      set(values, fieldId, initialValue ?? defaultInitialValue);

    setInitialValue(FORMIK_BUSINESS_FIELD_IDS.AU_NZ_BUSINESS_NUMBER, contact?.au_nz_business_number);
    setInitialValue(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_LEGAL_NAME, contact?.business_legal_name);
    setInitialValue(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_TRADING_NAME, contact?.business_trading_name);
    setInitialValue(FORMIK_BUSINESS_FIELD_IDS.AU_NZ_COMPANY_NUMBER, contact?.au_nz_company_number);
    setInitialValue(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_VALIDATION, null, null);

    return values;
  };

  const getValidationSchema = () => {
    const validations = {};

    const addValidation = (fieldId: string, fieldValidation: Yup.AnySchema) =>
      set(validations, fieldId, fieldValidation);

    addValidation(FORMIK_BUSINESS_FIELD_IDS.AU_NZ_BUSINESS_NUMBER, Yup.string());
    addValidation(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_LEGAL_NAME, Yup.string());
    addValidation(FORMIK_BUSINESS_FIELD_IDS.BUSINESS_TRADING_NAME, Yup.string());
    addValidation(FORMIK_BUSINESS_FIELD_IDS.AU_NZ_COMPANY_NUMBER, Yup.string());

    return validations;
  };

  const getValuesToCompare = (values: FormikValues) => {
    const omittedValues = omit(values, [FORMIK_BUSINESS_FIELD_IDS.BUSINESS_VALIDATION]);
    return getInitializedValues(omittedValues);
  };

  const isHiddenForContact = () => {
    if (!isCompany) return true;
    if (!gbgValidationStatus.is_business_validation_enabled) return true;
    const country = currentCountry?.toUpperCase();
    if (!country || !['AU', 'NZ'].includes(country)) return true;
    return false;
  };

  return {
    label: TEXTS.tabLabel,
    tabKey: BUSINESS_TAB_KEY,
    tabComponent: BusinessTab,
    getInitialValues,
    getValidationSchema,
    getValuesToCompare,
    isHiddenForContact,
  };
};

export default useBusinessTab;
